import * as Sentry from "@sentry/react";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return; // skip in local dev unless DSN is set

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // "development" | "production"
    // Only send errors in production by default; set to 1.0 during debugging
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 0,
    // Capture replays only for errors in production
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Don't send errors from browser extensions or third-party scripts
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
  });
}

/** Tag the current user in Sentry so errors include user context. */
export function identifySentryUser(id: string, email?: string) {
  Sentry.setUser({ id, email });
}

/** Clear user on logout. */
export function clearSentryUser() {
  Sentry.setUser(null);
}

export function captureOperationalError(
  error: unknown,
  operation: string,
  context: Record<string, string | number | boolean | undefined> = {},
) {
  Sentry.withScope((scope) => {
    scope.setTag("operation", operation);
    scope.setLevel("error");
    Object.entries(context).forEach(([key, value]) => {
      if (value !== undefined) scope.setExtra(key, value);
    });
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}

export { Sentry };
